<script setup lang="ts">
import { useFlowEditorContext } from '../composables/useFlowEditor'
import { WORLD_ORIGIN } from '../model/constants'
import { inputPorts, outputPorts } from '../model/ports'
import type { FlowNode } from '../model/types'

/** One node on the canvas with its input and output connection handles. */
defineProps<{ node: FlowNode; connectedPorts: Set<string> }>()

const editor = useFlowEditorContext()
const { selectedNodeIds } = editor.selection
const { previewActiveNodeIds } = editor.preview
const { onNodeMouseDown, startConnectionDrag, finishConnectionDrag } = editor.pointer
const { nodeTypeLabel, nodeSummary } = editor
</script>

<template>
  <article
    class="flow-node"
    :class="[
      `flow-node--${node.type}`,
      {
        'flow-node--selected': selectedNodeIds.includes(node.id),
        'flow-node--locked': node.locked,
        'flow-node--preview-active': previewActiveNodeIds.has(node.id),
      },
    ]"
    :style="{ left: `${node.x + WORLD_ORIGIN}px`, top: `${node.y + WORLD_ORIGIN}px` }"
    @mousedown.stop="onNodeMouseDown(node.id, $event)"
  >
    <button
      v-for="(port, index) in inputPorts(node)"
      :key="`input-${port}`"
      type="button"
      class="flow-handle flow-handle--input"
      :class="{
        'flow-handle--input-top': inputPorts(node).length === 2 && index === 0,
        'flow-handle--input-bottom': inputPorts(node).length === 2 && index === 1,
        'flow-handle--connected': connectedPorts.has(`${node.id}:${port}:input`),
        [`flow-handle--port-${index}`]:
          node.type === 'macro-start' || node.type === 'macro-end' || node.type === 'macro-call',
      }"
      :style="
        inputPorts(node).length > 2
          ? { top: `${(100 / (inputPorts(node).length + 1)) * (index + 1)}%` }
          : {}
      "
      :title="`${port}`"
      @mousedown.stop="startConnectionDrag(node.id, port, 'input', $event)"
      @mouseup.stop="finishConnectionDrag(node.id, port, 'input', $event)"
    />

    <button
      v-for="(port, index) in outputPorts(node)"
      :key="port"
      type="button"
      class="flow-handle flow-handle--output"
      :class="{
        'flow-handle--output-top': node.type === 'condition' && port === 'true',
        'flow-handle--output-bottom': node.type === 'condition' && port === 'else',
        'flow-handle--connected': connectedPorts.has(`${node.id}:${port}:output`),
        [`flow-handle--port-${index}`]:
          node.type === 'macro-start' || node.type === 'macro-end' || node.type === 'macro-call',
      }"
      :style="
        outputPorts(node).length > 1 && node.type !== 'condition'
          ? { top: `${(100 / (outputPorts(node).length + 1)) * (index + 1)}%` }
          : {}
      "
      :title="`${port}`"
      @mousedown.stop="startConnectionDrag(node.id, port, 'output', $event)"
      @mouseup.stop="finishConnectionDrag(node.id, port, 'output', $event)"
    >
      {{
        node.type === 'macro-start' || node.type === 'macro-end' || node.type === 'macro-call'
          ? index
          : port === 'next'
            ? ''
            : port
      }}
    </button>

    <header class="flow-node__header">
      <strong>{{ node.label }}</strong>
      <span>{{ nodeTypeLabel(node.type) }}</span>
    </header>

    <div class="flow-node__body">
      <p class="flow-node__summary">{{ nodeSummary(node) }}</p>
    </div>
  </article>
</template>

<style scoped>
.flow-node {
  position: absolute;
  z-index: 1;
  width: 260px;
  min-height: 130px;
  margin: 0;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 0.65rem;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  user-select: none;
  cursor: move;
}

.flow-node--selected {
  z-index: 2;
  border-color: #4338ca;
  box-shadow: 0 0 0 3px rgba(67, 56, 202, 0.2);
}

.flow-node__header {
  box-sizing: border-box;
  height: 36px;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 0.65rem 0.65rem 0 0;
  padding: 0.35rem 0.55rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  pointer-events: none;
}

.flow-node__header strong {
  font-size: 0.83rem;
}

.flow-node__header span {
  font-size: 0.71rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
}

.flow-node__body {
  box-sizing: border-box;
  position: relative;
  padding: 0.55rem;
  min-height: 90px;
  pointer-events: none;
}

.flow-node__summary {
  margin: 0;
  font-size: 0.84rem;
  color: #334155;
}

.flow-handle {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid #fca5a5;
  background: #fee2e2;
  padding: 0;
  font-size: 8px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  z-index: 4;
  transition:
    border-color 0.15s,
    background 0.15s;
}

.flow-handle--connected {
  border-color: #86efac;
  background: #dcfce7;
  color: #166534;
}

.flow-handle--input {
  left: -9px;
  top: calc(50% - 9px);
}

.flow-handle--input-top {
  top: calc(38% - 9px);
}

.flow-handle--input-bottom {
  top: calc(62% - 9px);
}

.flow-handle--output {
  right: -9px;
  top: calc(50% - 9px);
}

.flow-handle--output-top {
  top: calc(36% - 9px);
}

.flow-handle--output-bottom {
  top: calc(64% - 9px);
}

.flow-node--start {
  border-color: #22c55e;
}

.flow-node--start .flow-node__header {
  background: #ecfdf3;
  border-bottom-color: #bbf7d0;
}

.flow-node--end {
  border-color: #ef4444;
}

.flow-node--end .flow-node__header {
  background: #fef2f2;
  border-bottom-color: #fecaca;
}

.flow-node--macro-start {
  border-color: #a855f7;
}

.flow-node--macro-start .flow-node__header {
  background: #faf5ff;
  border-bottom-color: #e9d5ff;
}

.flow-node--macro-end {
  border-color: #7c3aed;
}

.flow-node--macro-end .flow-node__header {
  background: #f5f3ff;
  border-bottom-color: #ddd6fe;
}

.flow-node--macro-call {
  border-color: #8b5cf6;
}

.flow-node--macro-call .flow-node__header {
  background: #f5f3ff;
  border-bottom-color: #e0d7ff;
}

/* Macro port color palette — same index = same color on both sides */
.flow-handle--port-0 {
  background: #7c3aed;
  border-color: #5b21b6;
}

.flow-handle--port-1 {
  background: #2563eb;
  border-color: #1d4ed8;
}

.flow-handle--port-2 {
  background: #059669;
  border-color: #047857;
}

.flow-handle--port-3 {
  background: #d97706;
  border-color: #b45309;
}

.flow-handle--port-4 {
  background: #dc2626;
  border-color: #b91c1c;
}

.flow-handle--port-5 {
  background: #0891b2;
  border-color: #0e7490;
}

.flow-handle--port-6 {
  background: #db2777;
  border-color: #be185d;
}

.flow-handle--port-7 {
  background: #65a30d;
  border-color: #4d7c0f;
}

/* ── Preview panel ─────────────────────────────────────── */
.flow-node--preview-active {
  outline: 3px solid #22c55e;
  outline-offset: 3px;
  box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.18);
}
</style>
